import React, { useRef, useState, useEffect } from "react";
import Widget from "../../../widget/src/enqelt-react-widget/widget-example";
import {
  Box, Flex, Heading, Text, Tabs, TabList, TabPanels, Tab, TabPanel,
  useColorModeValue, VStack, useToast
} from "@chakra-ui/react";
import { useDisclosure } from "@chakra-ui/react";
import { 
  loadUserSettings, 
  saveSettingsToSlot, 
  getPrimaryUserRecord 
} from "../../../services/userSettingsService";
import defaultSettings from "../../../widget/src/enqelt-react-widget/settings.json";
import GuideModal from "../../../components/CreatewidgetPage/Guide";
import ContentTab from "../../../components/CreatewidgetPage/ContentTab";
import AppearanceTab from "../../../components/CreatewidgetPage/AppearanceTab";
import SizeTab from "../../../components/CreatewidgetPage/SizeTab.jsx";
import ActionButtons from "../../../components/CreatewidgetPage/ActionButton.jsx";
import PreviewHeader from "../../../components/CreatewidgetPage/HeaderPreview.jsx";
import SaveStatus from "../../../components/CreatewidgetPage/StatusIndicator";
import DataTab from "../../../components/CreatewidgetPage/DataTab.jsx";
import NewUserPopup from "../../../components/NewUserPopup";

export default function Dashboard() {
  // State variables
  const [settings, setSettings] = useState({
    ...defaultSettings,
    widgetHeightMultiplier: defaultSettings.widgetHeightMultiplier || 1,
    widthWidthMultiplier: defaultSettings.widthWidthMultiplier || 1
  });
  const [showGuide, setShowGuide] = useState(false);
  const [showNewUserPopup, setShowNewUserPopup] = useState(false);
  const fileInputRef = useRef(null);
  const [settingsUrl, setSettingsUrl] = useState('');
  const [isWidgetOpen, setIsWidgetOpen] = useState(true);
  const [heightMultiplier, setHeightMultiplier] = useState(settings.widgetHeightMultiplier || 1);
  const [widthMultiplier, setWidthMultiplier] = useState(settings.widgetWidthMultiplier || 1);
  const [activePreset, setActivePreset] = useState("custom");
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [saveStatus, setSaveStatus] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [isCloudSaving, setIsCloudSaving] = useState(false);
  const [isCloudLoading, setIsCloudLoading] = useState(false);
  const toast = useToast(); 
  const bgColor = useColorModeValue("#f8fafc", "gray.800");
  const panelBgColor = useColorModeValue("white", "gray.700");
  const accentColor = settings.color || "#44b474";
  const textColor = useColorModeValue("gray.700", "white");

  const handleCloseNewUserPopup = () => {
    localStorage.setItem("hasSeenWelcome", "true");
    setShowNewUserPopup(false);
  };

  const checkAndCreateUserRecord = async () => {
    try {
      // Check if user exists in database
      const userRecord = await getPrimaryUserRecord();
      
      // If user record exists and was retrieved successfully
      if (userRecord.success) {
        return false; // Settings not loaded yet
      }
      
      // User record doesn't exist or there was an error, try to create one
      setSaveStatus('Creating new user account...');
      
      try {
        // Use the default slot structure
        const result = await saveSettingsToSlot(
          defaultSettings,  // Default widget settings
          1,               // Save to slot 1
          "Default Save",  // Name for the slot
          true            // Set as active
        );
        
        if (result.success) {
          setSaveStatus('New account created with free trial status');
          setTimeout(() => setSaveStatus(''), 3000);
          
          // Check if the welcome popup has been shown before
          const hasSeenWelcome = localStorage.getItem("hasSeenWelcome");
          if (!hasSeenWelcome) {
            setShowNewUserPopup(true);
          }
          
          // Load the settings we just created
          try {
            await loadFromCloud();
            return true; // Settings were loaded
          } catch (cloudError) {
            console.error("Failed to load cloud settings after user creation:", cloudError);
            // Continue with local settings since cloud loading failed
          }
        } else {
          throw new Error(result.message || 'Unknown error saving settings');
        }
      } catch (createError) {
        console.error('Failed to create user record:', createError);
        setSaveStatus('Error creating user account - Using local mode');
        
        // IMPORTANT: Even if we can't create the user record in the database,
        // we should still let the user use the app with local storage only
        toast({
          title: "Limited functionality",
          description: "Could not connect to cloud. Changes will be saved locally only.",
          status: "warning",
          duration: 5000,
          isClosable: true,
        });
        
        // Use default settings but save to localStorage as fallback
        localStorage.setItem('settings', JSON.stringify(defaultSettings));
        setTimeout(() => setSaveStatus(''), 3000);
      }
      
      return false; 
      
    } catch (error) {
      console.error('Error checking user record:', error);
      
      // Show a more descriptive error message
      setSaveStatus('Error checking account - Using local mode');
      toast({
        title: "Connection issue",
        description: "Unable to connect to cloud services. Working in offline mode.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      
      setTimeout(() => setSaveStatus(''), 3000);
      return false;
    }
  };

  useEffect(() => {
    async function initializeUser() {
      setSaveStatus('Checking user account...');
      
      try {
        // First check if user exists in database and create if not
        const settingsLoaded = await checkAndCreateUserRecord();
        
        // Skip loading from localStorage if settings were already loaded from cloud
        if (settingsLoaded) {
          return; // Exit early since settings are already loaded
        }
        
        // Load from localStorage (either as primary source or as fallback)
        const localSettings = localStorage.getItem('settings');        
        if (localSettings) {          
          try {
            const parsedSettings = JSON.parse(localSettings);
            setSettings(parsedSettings);          
            if (parsedSettings.widgetHeightMultiplier) {
              setHeightMultiplier(parsedSettings.widgetHeightMultiplier);
            }          
            if (parsedSettings.widgetWidthMultiplier) {
              setWidthMultiplier(parsedSettings.widgetWidthMultiplier);
            }          
            setSaveStatus('Settings loaded from local storage');
            setTimeout(() => setSaveStatus(''), 2000);
            return; 
          } catch (parseError) {
            console.error("Error parsing localStorage settings:", parseError);
          }
        }
        
        // Try loading from cloud again as last resort
        try {
          await loadFromCloud();
        } catch (cloudError) {
          console.error("Failed to load cloud settings:", cloudError);
          // Fall back to default settings
          setSettings(defaultSettings);
          setSaveStatus('Using default settings');
          setTimeout(() => setSaveStatus(''), 2000);
        }
        
      } catch (error) {
        console.error('Failed to initialize user:', error);
        setSaveStatus('Error initializing user account - Using defaults');
        setTimeout(() => setSaveStatus(''), 2000);
        
        // Fall back to default settings if everything fails
        setSettings(defaultSettings);
      }
    }
    
    initializeUser();
  }, []);

  const loadFromCloud = async () => {
    setSaveStatus('Loading from cloud...');
    setIsCloudLoading(true);
    
    try {
      const result = await loadUserSettings(defaultSettings);
      
      if (result.success) {
        setSettings(result.settings);
        
        if (result.settings.widgetHeightMultiplier) {
          setHeightMultiplier(result.settings.widgetHeightMultiplier);
        }
        
        if (result.settings.widgetWidthMultiplier) {
          setWidthMultiplier(result.settings.widgetWidthMultiplier);
        }
        localStorage.setItem('settings', JSON.stringify(result.settings));
        
        setSaveStatus(`Settings loaded from ${result.source}`);
        setTimeout(() => setSaveStatus(''), 2000);
      } else {
        throw new Error(result.error || 'Unknown error loading settings');
      }
    } catch (error) {
      console.error('Failed to load cloud settings:', error);
      setSaveStatus('Error loading cloud settings');
      setTimeout(() => setSaveStatus(''), 2000);
    } finally {
      setIsCloudLoading(false);
    }
  };


  const triggerFileInput = () => {
    fileInputRef.current.click();
  };
  const handleSaveSettings = async (settingsToSave) => {
    setSaveStatus('Saving to cloud...');
    setIsCloudSaving(true);
    try {
      const result = await saveSettingsToSlot(settingsToSave, 1, "Default Save"); // Using slot 1 as default
      
      if (result.success) {
        setSaveStatus(`${result.message}`);
        setSettings(settingsToSave);        
        if (settingsToSave.widgetHeightMultiplier) {
          setHeightMultiplier(settingsToSave.widgetHeightMultiplier);
        }
        
        if (settingsToSave.widgetWidthMultiplier) {
          setWidthMultiplier(settingsToSave.widgetWidthMultiplier);
        }
      } else {
        throw new Error(result.message || 'Unknown error saving settings');
      }
    } catch (error) {
      console.error('Error saving settings:', error);
      setSaveStatus('Error: Failed to save settings to cloud');
    } finally {
      setIsCloudSaving(false);
      setTimeout(() => setSaveStatus(''), 3000);
    }
  };
  
  // Other functions...
  const resetWidgetSize = () => {
    const updatedSettings = { ...settings, widgetHeightMultiplier: 1, widgetWidthMultiplier: 1 };
    setSettings(updatedSettings);
    setHeightMultiplier(1);
    setWidthMultiplier(1);
    localStorage.setItem('settings', JSON.stringify(updatedSettings));
  };
  
  const handleColorChange = (event) => {
    const newColor = event.target.value;
    const updatedSettings = { ...settings, color: newColor };
    setSettings(updatedSettings);
    setActivePreset("custom");
    localStorage.setItem('settings', JSON.stringify(updatedSettings));
    updateSettingsUrl(updatedSettings);
  };

  const handleTextChange = (event, field) => {
    const newText = event.target.value;
    const updatedSettings = { ...settings, [field]: newText };
    setSettings(updatedSettings);
    setActivePreset("custom");
    localStorage.setItem('settings', JSON.stringify(updatedSettings));
    updateSettingsUrl(updatedSettings);
  };

  const handleAvatarChange = (event) => {
    const newAvatarUrl = event.target.value;
    const updatedSettings = { ...settings, botAvatar: newAvatarUrl };
    setSettings(updatedSettings);
    setActivePreset("custom");
    localStorage.setItem('settings', JSON.stringify(updatedSettings));
    updateSettingsUrl(updatedSettings);
  };

  const handleFontSizeChange = (value) => {
    const newFontSize = `${value}px`;
    const updatedSettings = { ...settings, fontSize: newFontSize };
    setSettings(updatedSettings);
    setActivePreset("custom");
    localStorage.setItem('settings', JSON.stringify(updatedSettings));
    updateSettingsUrl(updatedSettings);
  };

  const handleWidgetHeightMultiplierChange = (value) => {
    const updatedSettings = { ...settings, widgetHeightMultiplier: value };
    setSettings(updatedSettings);
    setHeightMultiplier(value);
    setActivePreset("custom");
    localStorage.setItem('settings', JSON.stringify(updatedSettings));
    updateSettingsUrl(updatedSettings);
  };

  const handleWidgetWidthMultiplierChange = (value) => {
    const updatedSettings = { ...settings, widgetWidthMultiplier: value };
    setSettings(updatedSettings);
    setWidthMultiplier(value);
    setActivePreset("custom");
    localStorage.setItem('settings', JSON.stringify(updatedSettings));
    updateSettingsUrl(updatedSettings);
  };

  const updateSettingsUrl = (updatedSettings) => {
    const settingsData = JSON.stringify(updatedSettings, null, 2);
    const blob = new Blob([settingsData], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    setSettingsUrl(url);
  };

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (!file) {
      setIsUploading(false); 
      return;
    }
    if (!file.type.startsWith('image/')) {
      setSaveStatus('Error: Please select an image file');
      setIsUploading(false); 
      return;
    }
  
    // Limit file size to 2MB
    const maxSizeInBytes = 2 * 1024 * 1024; // 2MB
    if (file.size > maxSizeInBytes) {
      setSaveStatus('Error: Image size must be less than 2MB');
      setIsUploading(false); 
      return;
    }    
    setSaveStatus('Uploading image...');  
    const reader = new FileReader();
    reader.onload = (e) => {
      const base64Image = e.target.result;      
      const updatedSettings = { ...settings, botAvatar: base64Image };
      setSettings(updatedSettings);
      setActivePreset("custom");
      localStorage.setItem('settings', JSON.stringify(updatedSettings));
      updateSettingsUrl(updatedSettings);
      setTimeout(() => {
        setIsUploading(false); 
        setSaveStatus('Image uploaded successfully!');
        setTimeout(() => setSaveStatus(''), 2000);
      }, 500);
    };
    reader.readAsDataURL(file);
  };

  const handleWidgetToggle = () => {
    setIsWidgetOpen(!isWidgetOpen);
  };

  const presets = [
    { name: "Green", color: "#44b474", fontSize: "14px" },
    { name: "Blue", color: "#3182ce", fontSize: "14px" },
    { name: "Purple", color: "#805ad5", fontSize: "14px" },
    { name: "Red", color: "#e53e3e", fontSize: "14px" },
  ];

  const applyPreset = (preset) => {
    const updatedSettings = { 
      ...settings, 
      color: preset.color,
      fontSize: preset.fontSize
    };
    setSettings(updatedSettings);
    localStorage.setItem('settings', JSON.stringify(updatedSettings));
    updateSettingsUrl(updatedSettings);
    setActivePreset(preset.name);
  };

  const safeHeightMultiplier = heightMultiplier || 1;
  const safeWidthMultiplier = widthMultiplier || 1;
  
  return (
    <Box bg={bgColor} minH="100vh" p={4}>
      <SaveStatus status={saveStatus} onClear={() => setSaveStatus('')} />
      
      {/* Add the NewUserPopup component properly in the return statement */}
      <NewUserPopup 
        isOpen={showNewUserPopup} 
        onClose={handleCloseNewUserPopup} 
        accentColor={accentColor} 
      />

      <Flex direction={{ base: "column", lg: "row" }} h="calc(100vh - 32px)" gap={6}>
        {/* Left panel - Settings */}
        <Box 
          w={{ base: "100%", lg: "400px" }} 
          bg={panelBgColor} 
          borderRadius="lg" 
          boxShadow="md"
          p={6}
          overflowY="auto"
        >
          <VStack spacing={6} align="stretch">
            <Box>
              <Heading size="md" mb={4} color={accentColor}>Widget Creator</Heading>
              <Text fontSize="sm" color={textColor} mb={4}>
                Customize your chat widget with the options below
              </Text>
            </Box>
  
            <Tabs variant="soft-rounded" colorScheme="green" isFitted>
              <TabList mb={4}>
                <Tab>Appearance</Tab>
                <Tab>Content</Tab>
                <Tab>Size</Tab>
                <Tab>Data</Tab>
              </TabList>

              <TabPanels>
                {/* Appearance Tab */}
                <TabPanel p={0}>
                  <AppearanceTab
                    settings={settings}
                    isWidgetOpen={isWidgetOpen}
                    handleWidgetToggle={handleWidgetToggle}
                    presets={presets}
                    activePreset={activePreset}
                    applyPreset={applyPreset}
                    handleColorChange={handleColorChange}
                    handleFontSizeChange={handleFontSizeChange}
                    handleAvatarChange={handleAvatarChange}
                    triggerFileInput={triggerFileInput}
                    fileInputRef={fileInputRef}
                    handleFileUpload={handleFileUpload}
                    setSettings={setSettings}
                    setActivePreset={setActivePreset}
                    updateSettingsUrl={updateSettingsUrl}
                    accentColor={accentColor}
                    isUploading={isUploading}
                    setIsUploading={setIsUploading}
                  />
                </TabPanel>

                {/* Content Tab */}
                <TabPanel p={0}>
                  <ContentTab 
                    settings={settings} 
                    handleTextChange={handleTextChange} 
                    accentColor={accentColor} 
                  />
                </TabPanel>

                {/* Size Tab */}
                <TabPanel p={0}>
                  <SizeTab
                    safeHeightMultiplier={safeHeightMultiplier}
                    safeWidthMultiplier={safeWidthMultiplier}
                    handleWidgetHeightMultiplierChange={handleWidgetHeightMultiplierChange}
                    handleWidgetWidthMultiplierChange={handleWidgetWidthMultiplierChange}
                    resetWidgetSize={resetWidgetSize}
                    accentColor={accentColor}
                  />
                </TabPanel>

                {/* Data Tab - New addition */}
                <TabPanel p={0}>
                  <DataTab 
                    accentColor={accentColor} 
                    settings={settings}
                    setSettings={setSettings}
                  />
                </TabPanel>
              </TabPanels>
            </Tabs>
            
            {/* Action Buttons with all functionality moved inside */}
            <ActionButtons
              settings={settings}
              defaultSettings={defaultSettings}
              setSettings={setSettings}
              setHeightMultiplier={setHeightMultiplier}
              setWidthMultiplier={setWidthMultiplier}
              setActivePreset={setActivePreset}
              setShowGuide={setShowGuide}
              accentColor={accentColor}
              updateSettingsUrl={updateSettingsUrl}
              setSaveStatus={setSaveStatus}
              isUploading={isUploading}
            />
            <GuideModal 
              isOpen={showGuide} 
              onClose={() => setShowGuide(false)} 
              accentColor={accentColor}
              settings={settings} // Pass settings to GuideModal
            />
          </VStack>
        </Box>
  
        {/* Right panel - Preview */}
        <Box 
          flex={1} 
          bg={panelBgColor} 
          borderRadius="lg" 
          boxShadow="md" 
          position="relative"
          overflow="hidden"
        >
          <PreviewHeader />
  
          <Box 
            position="absolute" 
            bottom={10} 
            right={100} 
            zIndex={2}
          >
            <Widget key={JSON.stringify(settings)} settings={settings} isOpen={isWidgetOpen} />
          </Box>
  
          {/* Background pattern */}
          <Box 
            position="absolute" 
            top={0} 
            left={0} 
            right={0} 
            bottom={0} 
            opacity={0.05} 
            backgroundImage="url('data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23000000' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E')"
          />
        </Box>
      </Flex>
    </Box>
  );
}